import React, { useState, useRef, useEffect } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import { Container, Row, Col, Card, Modal, Form, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  useFetchCalendarEventsQuery,
  useDeleteCalendarEventMutation,
  useCreateCalendarEventMutation,
  useUpdateCalendarEventMutation,
} from '../../store/apis/calendarAPI';
import './CalendarComponent.css';

function CalendarComponent() {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const [showModal, setShowModal] = useState(false);
  const [eventTitle, setEventTitle] = useState('');
  const [eventDescription, setEventDescription] = useState('');
  const [selectedInfo, setSelectedInfo] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [eventDate, setEventDate] = useState('');
  const calendarRef = useRef(null);

  // Get auth token from Redux store
  const userToken = useSelector((state) => state.auth.userToken);
  // Check if the store is still rehydrating
  const isRehydrating = useSelector((state) => state._persist?.rehydrated === false);

  // Use RTK Query hooks
  const {
    data: eventsData,
    isLoading,
    error,
    refetch,
  } = useFetchCalendarEventsQuery(
    {
      limit: 100,
      start_time__gte: new Date().toISOString().split('T')[0], // Format as YYYY-MM-DD
      ordering: 'start_time', // Sort by start time
    },
    {
      skip: !userToken || isRehydrating,
    },
  );

  const [deleteEventMutation] = useDeleteCalendarEventMutation();
  const [createEventMutation] = useCreateCalendarEventMutation();
  const [updateEventMutation] = useUpdateCalendarEventMutation();

  // Debug logging
  useEffect(() => {
    console.log('Calendar Component State:', {
      userToken: !!userToken,
      isRehydrating,
      isLoading,
      hasData: !!eventsData,
      dataLength: eventsData?.length || 0,
    });
  }, [userToken, isRehydrating, isLoading, eventsData]);

  // Convert API events to FullCalendar format
  const calendarEvents = React.useMemo(() => {
    if (!eventsData) {
      return [];
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return eventsData
      .filter((event) => new Date(event.start_time) >= today) // Filter out past events
      .map((event) => ({
        id: event.id,
        title: event.title,
        description: event.description,
        start: event.start_time,
        end: event.end_time,
        allDay: event.is_all_day,
        extendedProps: {
          description: event.description,
        },
      }));
  }, [eventsData]);

  const handleSelect = (selectInfo) => {
    setSelectedInfo(selectInfo);
    setShowModal(true);
    setIsEditing(false);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEventTitle('');
    setEventDescription('');
    setSelectedInfo(null);
    setIsEditing(false);
    setCurrentEvent(null);
  };

  const handleSaveEvent = async () => {
    if (eventTitle && selectedInfo) {
      try {
        const localDate = selectedInfo.start.toLocaleDateString('en-CA');
        const eventData = {
          title: eventTitle,
          description: eventDescription,
          start_time: `${localDate}T00:00:00Z`,
          end_time: `${localDate}T00:00:00Z`,
          is_all_day: true,
        };

        if (isEditing && currentEvent) {
          await updateEventMutation({
            id: currentEvent.id,
            ...eventData,
          }).unwrap();
        } else {
          await createEventMutation(eventData).unwrap();
        }

        handleCloseModal();
        refetch();
      } catch (error) {
        console.error('Error saving event:', error);
      }
    }
  };

  const handleEventClick = (clickInfo) => {
    setCurrentEvent(clickInfo.event);
    setEventTitle(clickInfo.event.title);
    setEventDescription(clickInfo.event.extendedProps.description || '');
    setSelectedInfo({
      start: clickInfo.event.start,
      end: clickInfo.event.end,
      allDay: true,
      view: { calendar: clickInfo.view.calendar },
    });
    setIsEditing(true);
    setShowModal(true);
  };

  const handleDeleteEvent = async () => {
    if (currentEvent) {
      try {
        await deleteEventMutation({ id: currentEvent.id }).unwrap();
        handleCloseModal();
        refetch();
      } catch (error) {
        console.error('Error deleting event:', error);
      }
    }
  };

  const handleTabularEventSubmit = async (e) => {
    e.preventDefault();
    if (eventTitle && eventDate && calendarRef.current) {
      try {
        const eventData = {
          title: eventTitle,
          description: '',
          start_time: `${eventDate}T00:00:00Z`,
          end_time: `${eventDate}T00:00:00Z`,
          is_all_day: true,
        };

        const result = await createEventMutation(eventData).unwrap();
        setCurrentEvent({
          id: result.id,
          title: result.title,
          start: new Date(result.start_time),
          end: new Date(result.end_time),
          allDay: result.is_all_day,
        });
        setEventDescription(result.description || '');
        setSelectedInfo({
          start: new Date(result.start_time),
          end: new Date(result.end_time),
          allDay: true,
          view: { calendar: calendarRef.current.getApi() },
        });
        setIsEditing(true);
        setShowModal(true);
        setEventDate('');
        refetch();
      } catch (error) {
        console.error('Error creating event:', error);
      }
    }
  };

  if (isLoading || isRehydrating) {
    return (
      <Container fluid className='calendar-wrapper'>
        <Row>
          <Col>
            <Card className='calendar-card'>
              <Card.Body
                className='d-flex justify-content-center align-items-center'
                style={{ minHeight: '400px' }}
              >
                <div className='text-center'>
                  <div className='spinner-border text-primary mb-3' role='status'>
                    <span className='visually-hidden'>Loading...</span>
                  </div>
                  <p className='mb-0'>{t('calendar.loading')}</p>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid className='calendar-wrapper'>
        <Row>
          <Col>
            <Card className='calendar-card'>
              <Card.Body
                className='d-flex justify-content-center align-items-center'
                style={{ minHeight: '400px' }}
              >
                <div className='text-center'>
                  <p className='text-danger mb-0'>{t('calendar.error')}</p>
                  <Button variant='outline-primary' className='mt-3' onClick={() => refetch()}>
                    {t('calendar.retry')}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid className='calendar-wrapper'>
      <Row>
        <Col>
          <Card className='calendar-card'>
            <Card.Body>
              <Form onSubmit={handleTabularEventSubmit} className='quick-add-form mb-4'>
                <Row>
                  <Col md={4}>
                    <Form.Group className='mb-3 mb-md-0'>
                      <Form.Label>{t('calendar.eventTitle')}</Form.Label>
                      <Form.Control
                        type='text'
                        value={eventTitle}
                        onChange={(e) => setEventTitle(e.target.value)}
                        placeholder={t('calendar.eventTitlePlaceholder')}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className='mb-3 mb-md-0'>
                      <Form.Label>{t('calendar.eventDate')}</Form.Label>
                      <Form.Control
                        type='date'
                        value={eventDate}
                        onChange={(e) => setEventDate(e.target.value)}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4} className='d-flex align-items-end'>
                    <Button variant='primary' type='submit' className='save-button w-100'>
                      {t('calendar.addEvent')}
                    </Button>
                  </Col>
                </Row>
              </Form>

              <FullCalendar
                ref={calendarRef}
                plugins={[dayGridPlugin, interactionPlugin, listPlugin]}
                initialView='dayGridMonth'
                headerToolbar={{
                  left: 'prev,next today',
                  center: 'title',
                  right: 'dayGridMonth,dayGridWeek,listYear',
                }}
                buttonText={{
                  today: t('calendar.buttonText.today'),
                  month: t('calendar.buttonText.month'),
                  week: t('calendar.buttonText.week'),
                  list: t('calendar.buttonText.list'),
                }}
                allDayText={t('calendar.buttonText.allDay')}
                events={calendarEvents}
                selectable={true}
                select={handleSelect}
                eventClick={handleEventClick}
                height='auto'
                locale={currentLanguage}
                dayMaxEvents={3}
                moreLinkContent={(args) => {
                  return `+${args.num} ${t('calendar.moreLinkText')}`;
                }}
                eventDisplay='block'
                eventTimeFormat={{
                  hour: '2-digit',
                  minute: '2-digit',
                  meridiem: false,
                }}
                eventDidMount={(info) => {
                  // Add tooltip to events
                  info.el.title = info.event.title;
                }}
                views={{
                  listYear: {
                    type: 'listYear',
                    duration: { years: 1 },
                    buttonText: t('calendar.buttonText.list'),
                    listDayFormat: {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    },
                    listDaySideFormat: false,
                  },
                }}
                initialDate={new Date()} // Set initial date to today
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={showModal} onHide={handleCloseModal} centered className='event-modal'>
        <Modal.Header closeButton className='event-modal-header'>
          <Modal.Title>
            {isEditing ? t('calendar.editEvent') : t('calendar.createEvent')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className='event-modal-body'>
          <Form>
            <Form.Group className='mb-3'>
              <Form.Label>{t('calendar.eventTitle')}</Form.Label>
              <Form.Control
                type='text'
                value={eventTitle}
                onChange={(e) => setEventTitle(e.target.value)}
                placeholder={t('calendar.eventTitlePlaceholder')}
                autoFocus
                className='event-input'
              />
            </Form.Group>
            <Form.Group className='mb-3'>
              <Form.Label>{t('calendar.eventDescription')}</Form.Label>
              <Form.Control
                as='textarea'
                rows={3}
                value={eventDescription}
                onChange={(e) => setEventDescription(e.target.value)}
                placeholder={t('calendar.eventDescriptionPlaceholder')}
                className='event-input'
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className='event-modal-footer'>
          {isEditing && (
            <Button variant='danger' onClick={handleDeleteEvent}>
              {t('calendar.delete')}
            </Button>
          )}
          <Button variant='secondary' onClick={handleCloseModal}>
            {t('calendar.cancel')}
          </Button>
          <Button variant='primary' onClick={handleSaveEvent} disabled={!eventTitle.trim()}>
            {t('calendar.save')}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default CalendarComponent;
